/*
 * Bulk sell simulator (S). Device-local selection only.
 * Does not touch Shopify cart, checkout, customer accounts, or PRICE BASE payloads.
 */
(() => {
  'use strict';

  const STORE_KEY = 'toreca:kaitori-selection:v1';
  const VERSION = 1;
  const MAX_ITEMS = 500;
  const QUANTITY = 1;
  const TTL_MS = 24 * 60 * 60 * 1000;
  const TITLE_MAX = 200;
  const AUX_MAX = 80;
  const CARD_PATH = /^\/pages\/kaitori\/card-[A-Za-z0-9][A-Za-z0-9_-]*$/;
  const SURFACES = {
    card_metaobject: true,
    pack_product_mirror: true,
    pending_metaobject: true,
  };
  const PB_URL = 'https://price-base.com/useful/toreca-kaitori';

  const FALLBACK_COPY = {
    add: 'まとめ売り候補に追加',
    added: '追加済み',
    review: '選択内容を確認',
    clear: 'すべて外す',
    close: '閉じる',
    remove: '候補から外す',
    tray_label: 'まとめ売り候補',
    tray_summary: '選択 {{ count }}種 / 合計{{ pieces }}枚',
    total_label: '選択時の参考合計',
    short_note:
      '選択時に表示されていた買取参考価格を単純に合計した目安です。実際の査定額や買取保証額ではありません。',
    full_note:
      '参考合計は、選択時に表示されていた各カードの当店調べの買取参考価格に数量を掛け、単純に合計した目安です。申込額・査定額・買取保証額ではありません。カードの状態、版・言語・加工、同一カードの数量、在庫状況、相場、査定時点などにより、実際の査定額は参考合計を上回る、下回る、または買取対象外となる場合があります。受付・実査定・買取契約は提携店 PRICE BASE が行います。最新の条件は PRICE BASE の案内をご確認ください。',
    no_handoff:
      '選択したカード、数量、参考合計は PRICE BASE へ自動送信されません。移動後、PRICE BASE の案内に従ってお申し込みください。',
    cta: 'こちらから PRICE BASE 宅配買取をご利用くださいませ。',
    cta_external: '外部サイトが新しいタブで開きます',
    save_unavailable: 'このブラウザでは選択内容を保存できません',
    expired: '保存されていた選択内容の期限が過ぎたため、消去しました',
    limit: '選択できるのは最大500種です',
    invalid_row: '一部の選択行を表示できませんでした',
    overflow: '数値の範囲を超えた行は合計から外しました',
    review_title: 'まとめ売り候補の確認',
    empty: '選択中のカードはありません',
    qty_label: '数量',
    unit_price_label: '選択時の参考単価',
    subtotal_label: '小計',
    observed_label: '価格確認日時',
    code_label: 'カード番号',
    rarity_label: 'レアリティ',
    status_added: 'まとめ売り候補に追加しました',
    status_removed: 'まとめ売り候補から外しました',
    status_cleared: 'まとめ売り候補をすべて外しました',
  };

  const nowIso = () => new Date().toISOString();

  const originOf = () => {
    try {
      return window.location.origin || '';
    } catch (error) {
      return '';
    }
  };

  const encodeObserved = (value) => String(value == null ? '' : value).replace(/-/g, '_');

  const decodeObserved = (value) => String(value == null ? '' : value).replace(/_/g, '-').trim();

  const clipText = (value, max) => {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return '';
    return text.length > max ? text.slice(0, max) : text;
  };

  const interpolate = (template, vars) =>
    String(template == null ? '' : template).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
      vars[key] == null ? '' : String(vars[key])
    );

  const formatYen = (value) => `¥${Number(value).toLocaleString('ja-JP')}`;

  const formatObserved = (value) => {
    const raw = decodeObserved(value);
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${y}/${m}/${d} ${hh}:${mm}`;
  };

  const normalizePath = (raw, origin = originOf()) => {
    if (typeof raw !== 'string') return '';
    const value = raw.trim();
    if (!value) return '';
    try {
      const url = new URL(value, origin || 'https://tcg-library.com');
      if (origin && url.origin !== origin) return '';
      const path = url.pathname.replace(/\/+$/, '');
      if (!CARD_PATH.test(path)) return '';
      return path;
    } catch (error) {
      return '';
    }
  };

  const validateItem = (input, origin) => {
    if (!input || typeof input !== 'object') return null;
    const path = normalizePath(input.path, origin);
    if (!path) return null;
    const title = clipText(input.title, TITLE_MAX);
    if (!title) return null;
    const unit = Number(input.unit_price_yen);
    if (!Number.isSafeInteger(unit) || unit <= 10) return null;
    const observed = decodeObserved(input.price_observed_at);
    if (!observed) return null;
    const quantity = QUANTITY;
    const surface = SURFACES[input.source_surface] ? input.source_surface : 'card_metaobject';
    const selectedAt = typeof input.selected_at === 'string' && input.selected_at.trim() ? input.selected_at.trim() : nowIso();
    const subtotal = unit * quantity;
    if (!Number.isSafeInteger(subtotal)) return null;
    return {
      path,
      title,
      card_code: clipText(input.card_code, AUX_MAX),
      rarity: clipText(input.rarity, AUX_MAX),
      unit_price_yen: unit,
      price_observed_at: observed,
      selected_at: selectedAt,
      quantity,
      source_surface: surface,
    };
  };

  const emptyState = () => ({ version: VERSION, updated_at: nowIso(), items: {} });

  const parseState = (raw, origin) => {
    const notices = [];
    if (raw == null || raw === '') return { state: emptyState(), notices, mutated: false };
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      notices.push('invalid_row');
      return { state: emptyState(), notices, mutated: true };
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || parsed.version !== VERSION) {
      notices.push('invalid_row');
      return { state: emptyState(), notices, mutated: true };
    }
    const updatedAt = Date.parse(parsed.updated_at);
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > TTL_MS) {
      notices.push('expired');
      return { state: emptyState(), notices, mutated: true, expired: true };
    }
    const sourceItems = parsed.items && typeof parsed.items === 'object' && !Array.isArray(parsed.items) ? parsed.items : {};
    const items = {};
    let dropped = false;
    Object.keys(sourceItems).forEach((key) => {
      if (Object.keys(items).length >= MAX_ITEMS) {
        dropped = true;
        return;
      }
      const item = validateItem(sourceItems[key], origin);
      if (!item) {
        dropped = true;
        return;
      }
      items[item.path] = item;
    });
    if (dropped) notices.push('invalid_row');
    return {
      state: { version: VERSION, updated_at: parsed.updated_at, items },
      notices,
      mutated: dropped,
    };
  };

  const computeTotals = (items) => {
    const rows = Object.keys(items).map((path) => items[path]);
    let kinds = 0;
    let pieces = 0;
    let total = 0;
    let overflow = false;
    const included = [];
    rows.forEach((item) => {
      const subtotal = item.unit_price_yen * item.quantity;
      if (
        !Number.isSafeInteger(item.unit_price_yen) ||
        !Number.isSafeInteger(item.quantity) ||
        !Number.isSafeInteger(subtotal) ||
        !Number.isSafeInteger(total + subtotal)
      ) {
        overflow = true;
        return;
      }
      kinds += 1;
      pieces += item.quantity;
      total += subtotal;
      included.push(item);
    });
    return { kinds, pieces, total, overflow, included, count: rows.length };
  };

  const readStorage = (storage) => {
    try {
      return storage.getItem(STORE_KEY);
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (storage, state) => {
    const payload = JSON.stringify({
      version: VERSION,
      updated_at: state.updated_at,
      items: state.items,
    });
    try {
      storage.setItem(STORE_KEY, payload);
      return true;
    } catch (error) {
      return false;
    }
  };

  const removeStorage = (storage) => {
    try {
      storage.removeItem(STORE_KEY);
      return true;
    } catch (error) {
      return false;
    }
  };

  const payloadFromButton = (button, origin) =>
    validateItem(
      {
        path: button.getAttribute('data-path') || '',
        title: button.getAttribute('data-title') || '',
        card_code: button.getAttribute('data-code') || '',
        rarity: button.getAttribute('data-rarity') || '',
        unit_price_yen: button.getAttribute('data-yen'),
        price_observed_at: button.getAttribute('data-obs') || '',
        selected_at: nowIso(),
        quantity: QUANTITY,
        source_surface: button.getAttribute('data-surface') || 'card_metaobject',
      },
      origin
    );

  const loadCopy = (root) => {
    const node = root.querySelector('[data-bulk-sim-copy]');
    if (!node) return { ...FALLBACK_COPY };
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      if (!parsed || typeof parsed !== 'object') return { ...FALLBACK_COPY };
      const copy = { ...FALLBACK_COPY };
      Object.keys(FALLBACK_COPY).forEach((key) => {
        if (typeof parsed[key] === 'string' && parsed[key]) copy[key] = parsed[key];
      });
      return copy;
    } catch (error) {
      return { ...FALLBACK_COPY };
    }
  };

  const specRow = (term, value, className) => {
    const wrap = document.createElement('div');
    wrap.className = 'bulk-sim__spec-row';
    const dt = document.createElement('dt');
    dt.textContent = term;
    const dd = document.createElement('dd');
    if (className) dd.className = className;
    dd.textContent = value;
    wrap.append(dt, dd);
    return wrap;
  };

  const initRoot = (root) => {
    if (root.dataset.bulkSimReady === 'true') return;
    root.dataset.bulkSimReady = 'true';

    const copy = loadCopy(root);
    const origin = originOf();
    const tray = root.querySelector('[data-bulk-sim-tray]');
    const dialog = root.querySelector('[data-bulk-sim-dialog]');
    const list = root.querySelector('[data-bulk-sim-list]');
    const empty = root.querySelector('[data-bulk-sim-empty]');
    const status = root.querySelector('[data-bulk-sim-status]');
    const summary = root.querySelector('[data-bulk-sim-summary]');
    const trayTotal = root.querySelector('[data-bulk-sim-total]');
    const dialogTotal = root.querySelector('[data-bulk-sim-dialog-total]');
    const cta = root.querySelector('[data-bulk-sim-cta]');
    const reviewBtn = root.querySelector('[data-bulk-sim-review]');
    const closeBtn = root.querySelector('[data-bulk-sim-close]');
    const trayCloseBtn = root.querySelector('[data-bulk-sim-tray-close]');
    const title = root.querySelector('#BulkSimReviewTitle');

    let storage = null;
    let persistable = true;
    try {
      storage = window.localStorage;
      const probe = `${STORE_KEY}:probe`;
      storage.setItem(probe, '1');
      storage.removeItem(probe);
    } catch (error) {
      storage = null;
      persistable = false;
    }

    let memory = emptyState();
    let announced = {};
    let trayDismissed = false;

    const announce = (message) => {
      if (!status || !message) return;
      status.textContent = '';
      status.textContent = message;
    };

    const noticeOnce = (key) => {
      if (!key || announced[key]) return;
      announced[key] = true;
      announce(copy[key] || '');
    };

    const hydrate = (raw, options) => {
      const parsed = parseState(raw, origin);
      memory = parsed.state;
      if (parsed.notices.includes('expired')) noticeOnce('expired');
      else if (parsed.notices.includes('invalid_row')) noticeOnce('invalid_row');
      if (persistable && storage && parsed.expired) {
        removeStorage(storage);
      } else if (persistable && storage && parsed.mutated && options && options.persistClean) {
        writeStorage(storage, memory);
      }
    };

    if (persistable && storage) hydrate(readStorage(storage), { persistClean: true });
    else noticeOnce('save_unavailable');

    const persist = () => {
      memory.updated_at = nowIso();
      if (!persistable || !storage) {
        noticeOnce('save_unavailable');
        return false;
      }
      const ok = writeStorage(storage, memory);
      if (!ok) {
        persistable = false;
        noticeOnce('save_unavailable');
      }
      return ok;
    };

    const addButtons = () => document.querySelectorAll('[data-bulk-sim-add]');
    const supportsSelection = () => addButtons().length > 0;

    const syncAddButtons = () => {
      addButtons().forEach((button) => {
        button.hidden = false;
        const path = normalizePath(button.getAttribute('data-path') || '', origin);
        const pressed = !!(path && memory.items[path]);
        button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        button.textContent = pressed ? copy.added : copy.add;
      });
    };

    const renderList = (included) => {
      if (!list) return;
      list.replaceChildren();
      included.forEach((item) => {
        const article = document.createElement('article');
        article.className = 'bulk-sim__item';

        const heading = document.createElement('h3');
        heading.className = 'bulk-sim__item-title';
        const link = document.createElement('a');
        link.className = 'bulk-sim__item-link';
        link.href = item.path;
        link.textContent = item.title;
        heading.appendChild(link);
        article.appendChild(heading);

        const spec = document.createElement('dl');
        spec.className = 'bulk-sim__spec';
        if (item.card_code) spec.appendChild(specRow(copy.code_label, item.card_code, 'callno'));
        if (item.rarity) spec.appendChild(specRow(copy.rarity_label, item.rarity));
        spec.appendChild(specRow(copy.unit_price_label, formatYen(item.unit_price_yen), 'tnum'));
        spec.appendChild(specRow(copy.qty_label, String(item.quantity), 'tnum'));
        spec.appendChild(specRow(copy.subtotal_label, formatYen(item.unit_price_yen * item.quantity), 'tnum'));
        if (item.price_observed_at) {
          spec.appendChild(specRow(copy.observed_label, formatObserved(item.price_observed_at), 'tnum'));
        }
        article.appendChild(spec);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'btn btn--ghost bulk-sim__remove';
        remove.textContent = copy.remove;
        remove.addEventListener('click', () => {
          applyChange(() => {
            delete memory.items[item.path];
            return 'status_removed';
          });
        });
        article.appendChild(remove);
        list.appendChild(article);
      });
    };

    const render = () => {
      const totals = computeTotals(memory.items);
      const hasItems = supportsSelection() && totals.count > 0 && !trayDismissed;
      if (tray) {
        tray.hidden = !hasItems;
        if (summary) {
          summary.textContent = interpolate(copy.tray_summary, {
            count: String(totals.kinds),
            pieces: String(totals.pieces),
          });
        }
        if (trayTotal) trayTotal.textContent = formatYen(totals.total);
      }
      if (dialogTotal) dialogTotal.textContent = formatYen(totals.total);
      if (empty) empty.hidden = hasItems;
      renderList(totals.included);
      syncAddButtons();
      if (cta) {
        cta.href = PB_URL;
        cta.target = '_blank';
        cta.rel = 'noopener noreferrer';
        cta.hidden = false;
      }
      document.documentElement.classList.toggle('bulk-sim-has-items', hasItems);
      const space = hasItems && tray ? `${tray.offsetHeight}px` : '0px';
      document.documentElement.style.setProperty('--bulk-sim-tray-space', space);
      if (totals.overflow) noticeOnce('overflow');
    };

    const applyChange = (mutator) => {
      const before = JSON.stringify(memory.items);
      const messageKey = mutator();
      if (before !== JSON.stringify(memory.items)) persist();
      render();
      if (messageKey) announce(copy[messageKey] || '');
    };

    const toggleFromButton = (button) => {
      const item = payloadFromButton(button, origin);
      if (!item) return;
      applyChange(() => {
        trayDismissed = false;
        if (memory.items[item.path]) {
          delete memory.items[item.path];
          return 'status_removed';
        }
        if (Object.keys(memory.items).length >= MAX_ITEMS) {
          announce(copy.limit);
          return '';
        }
        memory.items[item.path] = item;
        return 'status_added';
      });
    };

    const clearAll = () => {
      if (!Object.keys(memory.items).length) return;
      applyChange(() => {
        memory.items = {};
        return 'status_cleared';
      });
      if (dialog && dialog.open) dialog.close();
    };

    const openDialog = () => {
      if (!dialog || typeof dialog.showModal !== 'function') return;
      render();
      dialog.showModal();
      if (title) title.focus();
    };

    const closeDialog = () => {
      if (dialog && dialog.open) dialog.close();
    };

    addButtons().forEach((button) => {
      button.addEventListener('click', () => toggleFromButton(button));
    });
    root.querySelectorAll('[data-bulk-sim-clear]').forEach((button) => {
      button.addEventListener('click', clearAll);
    });
    if (reviewBtn) reviewBtn.addEventListener('click', openDialog);
    if (trayCloseBtn) {
      trayCloseBtn.addEventListener('click', () => {
        trayDismissed = true;
        render();
      });
    }
    if (closeBtn) closeBtn.addEventListener('click', closeDialog);
    if (dialog) {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      });
    }

    window.addEventListener('storage', (event) => {
      if (event.key !== STORE_KEY) return;
      hydrate(event.newValue, { persistClean: false });
      render();
    });

    render();
  };

  const init = () => document.querySelectorAll('[data-bulk-sim]').forEach(initRoot);

  window.TorecaBulkSellSim = {
    STORE_KEY,
    VERSION,
    MAX_ITEMS,
    QUANTITY,
    TTL_MS,
    PB_URL,
    encodeObserved,
    decodeObserved,
    normalizePath,
    validateItem,
    parseState,
    computeTotals,
    formatYen,
    interpolate,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-bulk-sim]').forEach(initRoot);
  });
})();
